import React, { useState, useEffect, useCallback, useRef } from "react";
import "../css/HeroSlider.css";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const HeroSlider = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const timeoutRef = useRef(null);
  const transitionTimer = useRef(null);
  const videoRefs = useRef({});

  const slides = [
    { type: "video", src: "/wp-content/uploads/2019/10/1 Min Video R1.mp4", title: null, subtitle: null },
    { type: "image", src: "/wp-content/uploads/2025/01/CRV Vessel.png", title: "DELIVERY OF CABLE LAYING AND REPAIR VESSEL, C/S SOPHIE GERMAIN", subtitle: "FIRST EVER CABLE LAYING AND REPAIR VESSEL TO EUROPE FROM SRI LANKA" },
    { type: "image", src: "/wp-content/uploads/2019/06/CABLE-LAYING-VESSEL.jpg", title: 'Colombo Dockyard PLC built Cable Laying Vessel "Cable Infinity"', subtitle: "SUCCESSFULLY DELIVERED TO HER OWNERS" },
    { type: "image", src: "/wp-content/uploads/2016/03/Aerial.png", title: "Colombo Dockyard", subtitle: "dry docks and repair berths" },
    { type: "image", src: "/wp-content/uploads/2025/01/Eco Bulker.png", title: "5,000 DWT HYBRID ECO BULK CARRIERS", subtitle: "BUILT FOR MISJE ECOBULK AS, NORWAY" },
    { type: "image", src: "/wp-content/uploads/2025/01/VLGC Surya Veerya.png", title: "VLGC SURYA VEERYA", subtitle: "DRYDOCK REPAIRS OF VERY LARGE GAS CARRIER" },
    { type: "image", src: "/wp-content/uploads/2025/01/Afloat Summary.jpg", title: "RAPID RESPONSE AFLOAT REPAIR SERVICES (RRARS)", subtitle: "@ COLOMBO, HAMBANTOTA, TRINCOMALEE AND GALLE" },
    { type: "image", src: "/wp-content/uploads/2019/03/image5.jpg", title: "Offshore Supply Vessel", subtitle: "capable of handling all specialist offshore tasks" },
    { type: "image", src: "/wp-content/uploads/2016/07/Pacific Jasper.png", title: "MT PACIFIC JASPER", subtitle: "FIRST SPECIAL SURVEY AND TRANSFORMING ACCOMMODATION FOR LUXURY LIVING" },
    { type: "image", src: "/wp-content/uploads/2016/06/35-m-Fisheries-Protection-Craft.jpg", title: "One of many Fisheries Protection Crafts", subtitle: "proudly built for the Maldivian Coast Guard" },
  ];

  useEffect(() => {
    slides.forEach((slide) => {
      if (slide.type === "image") {
        const img = new Image();
        img.src = slide.src;
      }
    });
  }, []);

  const changeSlide = useCallback(
    (newIndex) => {
      if (transitioning || newIndex === activeIndex) return;
      setNextIndex(newIndex);
      setTransitioning(true);
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
      transitionTimer.current = setTimeout(() => {
        setActiveIndex(newIndex);
        setNextIndex(null);
        setTransitioning(false);
      }, 600);
    },
    [activeIndex, transitioning]
  );

  const nextSlide = useCallback(() => {
    changeSlide((activeIndex + 1) % slides.length);
  }, [activeIndex, changeSlide]);

  const prevSlide = () => {
    changeSlide((activeIndex - 1 + slides.length) % slides.length);
  };

  useEffect(() => {
    if (transitioning) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const current = slides[activeIndex];
    if (current.type !== "video") {
      timeoutRef.current = setTimeout(() => nextSlide(), 8000);
    }
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [activeIndex, transitioning, slides, nextSlide]);

  useEffect(() => {
    const video = videoRefs.current[activeIndex];
    if (video && slides[activeIndex].type === "video") {
      const onEnded = () => { if (!transitioning) nextSlide(); };
      video.addEventListener("ended", onEnded);
      video.play().catch((e) => console.log("Video play failed", e));
      return () => {
        video.removeEventListener("ended", onEnded);
        video.pause();
        video.currentTime = 0;
      };
    }
  }, [activeIndex, transitioning, slides, nextSlide]);

  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([idx, video]) => {
      if (video && parseInt(idx) !== activeIndex) video.pause();
    });
  }, [activeIndex]);

  const getOpacity = (idx) => {
    if (idx === activeIndex) return transitioning ? 0 : 1;
    if (idx === nextIndex) return transitioning ? 1 : 0;
    return 0;
  };

  const getZIndex = (idx) => {
    if (idx === activeIndex) return 10;
    if (idx === nextIndex) return 20;
    return 5;
  };

  const captionSlide = transitioning && nextIndex !== null ? slides[nextIndex] : slides[activeIndex];
  const showCaption = captionSlide && captionSlide.title;

  return (
    <section id="main-banner">
      <div className="hero-slider-container">
        {slides.map((slide, idx) => (
          <div
            key={idx}
            className="hero-slide"
            style={{
              opacity: getOpacity(idx),
              zIndex: getZIndex(idx),
            }}
          >
            {slide.type === "video" ? (
              <video
                ref={(el) => (videoRefs.current[idx] = el)}
                src={slide.src}
                muted
                playsInline
                className="hero-slide-media"
              />
            ) : (
              <img
                src={slide.src}
                alt={`Slide ${idx}`}
                className="hero-slide-media"
              />
            )}
          </div>
        ))}

        {showCaption && (
          <div className="hero-caption-box hero-caption">
            <h2>{captionSlide.title}</h2>
            <h3>{captionSlide.subtitle}</h3>
          </div>
        )}

        <button onClick={prevSlide} className="hero-nav-btn hero-nav-prev" aria-label="Previous slide">
          <FiChevronLeft />
        </button>
        <button onClick={nextSlide} className="hero-nav-btn hero-nav-next" aria-label="Next slide">
          <FiChevronRight size={24} />
        </button>

        <div className="hero-dots">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => changeSlide(idx)}
              className={`hero-dot ${idx === activeIndex ? "active" : ""}`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSlider;
